import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getTransfers } from '../services/transferService';
import TrasladoDetalleModal from '../components/TrasladoDetalleModal';

export default function Traslados() {
  const [traslados, setTraslados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [query, setQuery] = useState('');
  const [filterDestino, setFilterDestino] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [detalleTraslado, setDetalleTraslado] = useState(null);

  const loadData = useCallback(() => {
    setLoading(true);
    setError('');
    getTransfers()
      .then(data => setTraslados(Array.isArray(data) ? data : []))
      .catch(() => setError('No se pudo cargar el listado de traslados.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const sucursalesDestino = useMemo(() =>
    [...new Set(traslados.map(t => t.branchDest?.name).filter(Boolean))].sort(),
    [traslados]
  );

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return traslados.filter(t => {
      const matchQuery = !q
        || t.details?.some(d => d.product?.name?.toLowerCase().includes(q) || d.equipment?.name?.toLowerCase().includes(q))
        || t.branchOrigin?.name?.toLowerCase().includes(q)
        || t.branchDest?.name?.toLowerCase().includes(q);
      const matchDestino = !filterDestino || t.branchDest?.name === filterDestino;
      const fecha = t.date?.slice(0, 10);
      const matchDesde = !fechaDesde || fecha >= fechaDesde;
      const matchHasta = !fechaHasta || fecha <= fechaHasta;
      return matchQuery && matchDestino && matchDesde && matchHasta;
    });
  }, [traslados, query, filterDestino, fechaDesde, fechaHasta]);

  function clearFilters() {
    setQuery('');
    setFilterDestino('');
    setFechaDesde('');
    setFechaHasta('');
  }

  const hasFilters = query || filterDestino || fechaDesde || fechaHasta;

  return (
    <div className="page-fade" style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 22 }}>
        <p style={{ fontSize: 12, color: 'var(--orig-text-muted)', marginBottom: 3 }}>
          Movimientos de inventario entre sucursales
        </p>
        <h5 style={{
          fontWeight: 800, color: 'var(--orig-text)',
          fontFamily: 'var(--orig-font-display)', letterSpacing: 0.5, fontSize: 20,
        }}>
          Consulta de Traslados
        </h5>
      </div>

      {/* Filters */}
      <div className="section-card" style={{ marginBottom: 20 }}>
        <div className="section-header">
          <h6>
            <i className="bi bi-funnel" style={{ color: 'var(--orig-green)' }} />
            Filtros de búsqueda
          </h6>
        </div>
        <div className="section-body" style={{ padding: '16px 20px' }}>
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-4">
              <label className="form-label">Producto o sucursal</label>
              <div className="search-wrap">
                <i className="bi bi-search search-icon" />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Ej: Latas Grandes..."
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                />
                {query && (
                  <button className="clear-btn" onClick={() => setQuery('')} title="Limpiar">
                    <i className="bi bi-x-lg" />
                  </button>
                )}
              </div>
            </div>

            <div className="col-6 col-md-3">
              <label className="form-label">Sucursal destino</label>
              <select
                className="form-select"
                value={filterDestino}
                onChange={e => setFilterDestino(e.target.value)}
              >
                <option value="">Todas</option>
                {sucursalesDestino.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <div className="col-6 col-md-2">
              <label className="form-label">Desde</label>
              <input
                type="date"
                className="form-control"
                value={fechaDesde}
                onChange={e => setFechaDesde(e.target.value)}
              />
            </div>

            <div className="col-6 col-md-2">
              <label className="form-label">Hasta</label>
              <input
                type="date"
                className="form-control"
                value={fechaHasta}
                onChange={e => setFechaHasta(e.target.value)}
              />
            </div>

            {hasFilters && (
              <div className="col-6 col-md-1">
                <button
                  className="btn-orig btn-orig-outline"
                  onClick={clearFilters}
                  style={{ width: '100%', justifyContent: 'center', padding: '9px 10px' }}
                  title="Limpiar filtros"
                >
                  <i className="bi bi-x-circle" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="section-card">
        <div className="section-header">
          <h6>
            <i className="bi bi-arrow-left-right" style={{ color: 'var(--orig-green)' }} />
            Traslados
          </h6>
          {!loading && (
            <span className="badge-orig badge-green">
              {filtered.length} {filtered.length === 1 ? 'registro' : 'registros'}
            </span>
          )}
        </div>

        <div style={{ overflowX: 'auto' }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--orig-text-muted)' }}>
              <i className="bi bi-arrow-repeat" style={{ fontSize: 28, display: 'block', marginBottom: 10, animation: 'spin 1s linear infinite' }} />
              Cargando traslados...
            </div>
          ) : error ? (
            <div style={{ padding: '28px 20px' }}>
              <div className="alert-orig alert-error">
                <i className="bi bi-exclamation-triangle-fill" />
                {error}
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--orig-text-muted)' }}>
              <i className="bi bi-search" style={{ fontSize: 30, display: 'block', marginBottom: 10, opacity: 0.4 }} />
              <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Sin resultados</div>
              <div style={{ fontSize: 12 }}>
                {hasFilters ? 'No hay traslados que coincidan con los filtros.' : 'No hay traslados registrados.'}
              </div>
              {hasFilters && (
                <button
                  className="btn-orig btn-orig-ghost"
                  onClick={clearFilters}
                  style={{ marginTop: 14, fontSize: 12 }}
                >
                  <i className="bi bi-x-circle" />
                  Limpiar filtros
                </button>
              )}
            </div>
          ) : (
            <table className="orig-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Fecha</th>
                  <th>Origen</th>
                  <th>Destino</th>
                  <th>Productos</th>
                  <th>Observaciones</th>
                  <th>Detalle</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(t => {
                  const totalQty = (t.details || []).reduce((s, d) => s + (d.quantity || 0), 0);
                  return (
                    <tr key={t.id}>
                      <td style={{ color: 'var(--orig-text-muted)', fontSize: 12, fontFamily: 'monospace' }}>
                        {t.id}
                      </td>
                      <td style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>
                        {t.date?.slice(0, 10)}
                      </td>
                      <td style={{ fontWeight: 500 }}>
                        {t.branchOrigin?.name}
                      </td>
                      <td style={{ fontWeight: 500 }}>
                        {t.branchDest?.name}
                      </td>
                      <td>
                        <div style={{ fontWeight: 500 }}>
                          {(t.details || []).length} {(t.details || []).length === 1 ? 'línea' : 'líneas'}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--orig-amber-light)' }}>
                          {totalQty} unidades
                        </div>
                      </td>
                      <td style={{ fontSize: 12.5, color: 'var(--orig-text-muted)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.observations || '—'}
                      </td>
                      <td>
                        <button
                          className="btn-orig btn-orig-outline"
                          onClick={() => setDetalleTraslado(t)}
                          style={{ padding: '5px 12px', fontSize: 12 }}
                          title="Ver detalle del traslado"
                        >
                          <i className="bi bi-eye" />
                          Ver
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {detalleTraslado && (
        <TrasladoDetalleModal
          traslado={detalleTraslado}
          onClose={() => setDetalleTraslado(null)}
        />
      )}
    </div>
  );
}
